import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Alert } from '../components/ui/Alert';
import axios from '../utils/axios';

const VerifyOTP: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { email, purpose = 'verification' } = location.state || {};
  
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (!email) {
      navigate('/login');
    }
  }, [email, navigate]);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newOtp = [...otp];
    for (let i = 0; i < pastedData.length; i++) {
      newOtp[i] = pastedData[i];
    }
    setOtp(newOtp);
    inputRefs.current[Math.min(pastedData.length, 5)]?.focus();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    const otpCode = otp.join('');
    if (otpCode.length !== 6) {
      setError('Please enter the complete 6-digit OTP');
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post('/api/auth/verify-otp', { 
        email, 
        otp: otpCode 
      });
      
      if (response.data.purpose === 'reset') {
        navigate('/reset-password', { state: { email, otp: otpCode } });
      } else {
        setSuccess(true);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Invalid OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendCooldown > 0) return;
    
    setIsResending(true);
    setError('');

    try {
      await axios.post('/api/auth/resend-otp', { email, purpose });
      setResendCooldown(60);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to resend OTP. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  if (!email) return null;

  return (
    <div className="split-layout">
      <div className="split-layout-left">
        <div className="branding">
          <div className="branding-logo">
            <h1>MavScope</h1>
            <p>AI-Powered Roof & Siding Measurements</p>
          </div>
          
          <div className="branding-features">
            <div className="branding-feature">
              <div className="branding-feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              </div>
              <div className="branding-feature-text">
                <h3>Secure Verification</h3>
                <p>Enter the 6-digit code sent to your email or phone to verify your identity.</p>
              </div>
            </div>
            
            <div className="branding-feature">
              <div className="branding-feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
              </div>
              <div className="branding-feature-text">
                <h3>Quick & Easy</h3>
                <p>OTP expires in 10 minutes. Request a new code if needed.</p>
              </div>
            </div>
            
            <div className="branding-feature">
              <div className="branding-feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
              </div>
              <div className="branding-feature-text">
                <h3>Account Protection</h3>
                <p>OTP verification helps protect your account from unauthorized access.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="split-layout-right">
        <div style={{ width: '100%', maxWidth: '400px' }}>
          <div className="mobile-logo">
            <h1>MavScope</h1>
            <p>Verify Your Account</p>
          </div>
          
          <div className="form-card">
            {success ? (
              <>
                <div className="form-header">
                  <div style={{ 
                    width: '64px', 
                    height: '64px', 
                    borderRadius: '50%', 
                    backgroundColor: 'var(--color-success-light)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto var(--spacing-4)'
                  }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="2" width="32" height="32">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                  <h2 className="form-title">Verified!</h2>
                  <p className="form-description">
                    Your account has been verified successfully. You now have full access to all features.
                  </p>
                </div>
                
                <div className="form-actions">
                  <Link to="/" style={{ width: '100%' }}>
                    <Button variant="primary" size="lg" fullWidth>
                      Go to Dashboard
                    </Button>
                  </Link>
                </div>
              </>
            ) : (
              <>
                <div className="form-header">
                  <h2 className="form-title">Enter verification code</h2>
                  <p className="form-description">
                    We sent a 6-digit code to <strong>{email}</strong>
                  </p>
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="form-fields">
                    <div style={{ 
                      display: 'flex', 
                      gap: 'var(--spacing-2)', 
                      justifyContent: 'center',
                      marginBottom: 'var(--spacing-4)'
                    }}>
                      {otp.map((digit, index) => (
                        <input
                          key={index}
                          ref={el => { inputRefs.current[index] = el; }}
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          value={digit}
                          onChange={(e) => handleChange(index, e.target.value)}
                          onKeyDown={(e) => handleKeyDown(index, e)}
                          onPaste={index === 0 ? handlePaste : undefined}
                          style={{
                            width: '48px',
                            height: '56px',
                            textAlign: 'center',
                            fontSize: 'var(--text-2xl)',
                            fontWeight: 'var(--font-semibold)',
                            border: '1px solid var(--color-gray-300)',
                            borderRadius: 'var(--radius-lg)',
                            outline: 'none',
                            transition: 'all var(--transition-fast)'
                          }}
                          autoFocus={index === 0}
                        />
                      ))}
                    </div>

                    {error && <Alert variant="error">{error}</Alert>}
                  </div>

                  <div className="form-actions">
                    <Button 
                      type="submit" 
                      variant="primary"
                      size="lg"
                      fullWidth
                      isLoading={isLoading}
                    >
                      Verify Code
                    </Button>
                  </div>
                </form>

                <div style={{ textAlign: 'center', marginTop: 'var(--spacing-4)' }}>
                  <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-500)', marginBottom: 'var(--spacing-2)' }}>
                    Didn't receive the code?
                  </p>
                  <button
                    onClick={handleResendOTP}
                    disabled={resendCooldown > 0 || isResending}
                    className="link"
                    style={{ 
                      background: 'none', 
                      border: 'none', 
                      cursor: resendCooldown > 0 ? 'default' : 'pointer',
                      opacity: resendCooldown > 0 ? 0.5 : 1
                    }}
                  >
                    {isResending ? 'Sending...' : resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Code'}
                  </button>
                </div>

                <div className="form-footer">
                  <p>
                    <Link to="/login" className="link">Back to Sign In</Link>
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyOTP;
